import React, { useState, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { TextField, Button, Alert, Box, Typography } from '@mui/material'
import { WrappedCard as RegistrationCard } from '../registration/RegistrationCard'
import PasswordInput from '../registration/PasswordInput'
import GoogleLoginButton from '../login/GoogleLoginButton'
import MicrosoftLoginButton from '../login/MicrosoftLoginButton'

const PasswordForm = ({ code, isContact, setBadCodeError }) => {
  const navigate = useNavigate()

  const [mismatchError, setMismatchError] = useState(false)
  const [passwordReqError, setPasswordReqError] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const [password, setPassword] = useState('')
  const [confirmedPassword, setConfirmedPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const onSubmit = () => {
    if (password !== confirmedPassword) setMismatchError(true)
    setSubmitError(false)
    axios
      .post('/api/registration/account', {
        password,
        code,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
      })
      .then((res) => {
        const destination = isContact ? '/setup/logo' : '/dashboard'
        navigate(destination)
      })
      .catch((error) => {
        if (error.response.status === 401) return setBadCodeError(true)
        if (
          error.response.status === 400 &&
          error.response.data.error === 'ACCOUNT_EXISTS'
        ) {
          window.location.replace('/login?error=account_exists')
        }
        setSubmitError(true)
      })
  }

  const validateForm = () => {
    if (!(password && confirmedPassword)) return false
    if (!isContact && !(firstName && lastName)) return false

    if (password.length < 10 || confirmedPassword.length < 10) return false

    if (password !== confirmedPassword) return false

    return true
  }

  return (
    <form style={{ display: 'flex', justifyContent: 'center' }}>
      <div className='create-account-step'>
        {submitError && (
          <Alert severity='error'>
            Something went wrong. Please try again later
          </Alert>
        )}

        <div className='inputs'>
          {!isContact && (
            <>
              <TextField
                autoFocus={!isContact}
                fullWidth
                margin={'dense'}
                value={firstName}
                label='First Name'
                onChange={(e) => {
                  setFirstName(e.target.value)
                }}
              />
              <TextField
                fullWidth
                margin={'dense'}
                value={lastName}
                label='Last Name'
                onChange={(e) => {
                  setLastName(e.target.value)
                }}
              />
            </>
          )}

          <PasswordInput
            password={password}
            confirmedPassword={confirmedPassword}
            mismatchError={mismatchError}
            setMismatchError={setMismatchError}
            setPassword={setPassword}
            passwordReqError={passwordReqError}
            setPasswordReqError={setPasswordReqError}
            setConfirmedPassword={setConfirmedPassword}
            autoFocus
          ></PasswordInput>
        </div>

        <div className='buttons'>
          <Button
            type='submit'
            variant='contained'
            onClick={(e) => {
              e.preventDefault()
              onSubmit()
            }}
            disabled={!validateForm()}
          >
            Submit
          </Button>
        </div>
      </div>
    </form>
  )
}

const IDPSelectorPage = ({ code, onNext, isContact }) => {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
    >
      <Button variant={'outlined'} onClick={onNext}>
        Create an account with a password
      </Button>
      <Box sx={{ mb: 2, mt: 1 }}>or</Box>
      <GoogleLoginButton code={code} isContact={isContact} />
      <MicrosoftLoginButton code={code} isContact={isContact} />
    </Box>
  )
}

const Claim = ({ lionsClub, contactOverride, text, claimOverride }) => {
  const [page, setPage] = useState(0)

  const code = claimOverride || useParams().code
  const { search } = useLocation()
  const queryParams = useMemo(() => new URLSearchParams(search), [search])
  const isContact = contactOverride || queryParams.get('is_contact') === 'true'

  const [badCodeError, setBadCodeError] = useState(
    queryParams.get('is_invalid_code') === 'true'
  )

  const isEmailMismatch = queryParams.get('error') === 'email_mismatch'

  const title = isContact
    ? 'Finish setting up your account'
    : 'Set up your account'

  return (
    <RegistrationCard title={title}>
      <Box sx={{ mb: 2, mx: 2 }}>{lionsClub}</Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {text}
      </Box>

      {badCodeError && (
        <>
          <Alert severity='error' sx={{ mb: 2 }}>
            The link that you used is expired or invalid. Please ask your
            administrator to resend your invite.
          </Alert>
          <Typography>
            <Button onClick={() => window.location.replace('/login')}>
              If you have already logged in before, click here to login
            </Button>
          </Typography>
        </>
      )}
      {isEmailMismatch && (
        <Alert severity='error' sx={{ mb: 2 }}>
          The email address provided does not match our records. Please log in
          with the correct account.
        </Alert>
      )}

      {!badCodeError && (
        <>
          {page === 0 && (
            <IDPSelectorPage
              code={code}
              onNext={() => setPage(1)}
              isContact={isContact}
            />
          )}
          {page === 1 && (
            <PasswordForm
              code={code}
              isContact={isContact}
              setBadCodeError={setBadCodeError}
            />
          )}
        </>
      )}
    </RegistrationCard>
  )
}

export default Claim
